export const getAlbumCaption = ({
    albumCount,
    photoCount,
    translate
}: {
    albumCount?: number;
    photoCount: number;
    translate: SpAngularJs.ITranslateFilter;
}) => {
    if (albumCount !== undefined && albumCount > 0) {
        return translate(albumCount === 1 ? 'oneAlbum' : 'multipleAlbums', {
            albumCount: String(albumCount)
        });
    }

    if (!photoCount) {
        return translate('zeroPhotos');
    }

    return translate(photoCount === 1 ? 'onePhoto' : 'multiplePhotos', {
        photoCount: String(photoCount)
    });
};

/**
 * Scopes a translate filter to the download-destination strings, so components translating
 * bare keys can still reach them.
 */
export const getDownloadDestinationsTranslate =
    (translate: SpAngularJs.ITranslateFilter): SpAngularJs.ITranslateFilter =>
    (id, interpolateParams) =>
        translate(`client.gallery.downloadDestinations.${id}`, interpolateParams);

const getCountPhrase = (
    count: number,
    keyPrefix: 'photoCount' | 'videoCount' | 'slideshowCount',
    translate: SpAngularJs.ITranslateFilter
) => {
    if (count < 1) {
        return null;
    }

    if (count === 1) {
        return translate(`${keyPrefix}One`);
    }

    return translate(`${keyPrefix}Many`, { [keyPrefix]: String(count) });
};

/**
 * Joins 2 or 3 already-translated phrases naming what a download contains, e.g. "12 Photos" +
 * "1 Video" -> "12 Photos and 1 Video". Uses positional keys (`mixedCount`/`mixedCountThree`)
 * since which types are present varies, unlike the per-type keys above.
 */
const joinCountPhrases = (
    phrases: Array<string | null>,
    translate: SpAngularJs.ITranslateFilter
): string | null => {
    const present = phrases.filter((phrase): phrase is string => Boolean(phrase));

    if (present.length === 2) {
        return translate('mixedCount', { first: present[0], second: present[1] });
    }

    if (present.length === 3) {
        return translate('mixedCountThree', {
            first: present[0],
            second: present[1],
            third: present[2]
        });
    }

    return present[0] ?? null;
};

/**
 * Names what a download contains: "12 Photos", "1 Video", "12 Photos and 3 Videos", "1 Photo,
 * 2 Videos and 1 Slideshow". Null when there is nothing to name, which callers read as "keep the
 * generic copy".
 */
export const getMediaCountCaption = ({
    photoCount,
    videoCount,
    slideshowCount = 0,
    translate
}: {
    photoCount: number;
    videoCount: number;
    slideshowCount?: number;
    translate: SpAngularJs.ITranslateFilter;
}) => {
    const photos = getCountPhrase(photoCount, 'photoCount', translate);
    const videos = getCountPhrase(videoCount, 'videoCount', translate);
    const slideshows = getCountPhrase(slideshowCount, 'slideshowCount', translate);

    return joinCountPhrases([photos, videos, slideshows], translate);
};
